import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { TrendingUp, Users, Package, AlertTriangle, DollarSign, Award, UserCheck, Percent, Download, RefreshCw } from "lucide-react";

export default function Gerencia() {
  const [d, setD] = useState({});
  const [tiposCliente, setTiposCliente] = useState([]);
  const [margenes, setMargenes] = useState({}); // clave → margen
  const [plantillas, setPlantillas] = useState([]);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    api.get("/gerencia/dashboard").then(r => setD(r.data));
    api.get("/masters/catalog/tipos_cliente").then(r => setTiposCliente(r.data || []));
    api.get("/margenes/tipo-cliente").then(r => {
      const m = {};
      (r.data || []).forEach(x => { m[x.tipo_cliente_clave] = x.margen; });
      setMargenes(m);
    });
    api.get("/plantillas").then(r => setPlantillas(r.data || []));
  }, []);

  const saveMargen = async (clave, valor) => {
    if (valor === "" || valor === null || isNaN(parseFloat(valor))) return;
    setSavingKey(clave);
    try {
      await api.post("/margenes/tipo-cliente", { tipo_cliente_clave: clave, margen: parseFloat(valor) });
      toast.success(`Margen guardado para ${clave}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingKey(null); }
  };

  const downloadPlantilla = async (key, filename) => {
    try {
      const r = await api.get(`/plantillas/${key}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const regenerarPlantillas = async () => {
    try {
      await api.post("/plantillas/regenerar");
      toast.success("Plantillas regeneradas con datos actuales");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="gerencia-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Gerencia</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Panel Ejecutivo</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Indicadores clave consolidados del mes y la operación actual.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={DollarSign} label="Ventas del mes" value={`$${(d.ventas_mes_monto ?? 0).toFixed(2)}`} sub={`${d.ventas_mes_pedidos ?? 0} pedidos`} tone="primary" />
        <Kpi icon={TrendingUp} label="CxC total" value={`$${(d.cxc_total ?? 0).toFixed(2)}`} sub={`${(d.cxc_vencida ?? 0) > 0 ? `$${d.cxc_vencida.toFixed(2)} vencida` : "Al día"}`} tone={d.cxc_vencida > 0 ? "danger" : "default"} />
        <Kpi icon={TrendingUp} label="CxP total" value={`$${(d.cxp_total ?? 0).toFixed(2)}`} sub="Por pagar" tone="warning" />
        <Kpi icon={Package} label="Lotes activos" value={d.inventario_lotes_activos ?? 0} sub={`${d.inventario_criticos_30d ?? 0} por vencer`} tone="default" />
        <Kpi icon={AlertTriangle} label="Vencidos" value={d.inventario_vencidos ?? 0} sub="Gestionar urgente" tone="danger" />
        <Kpi icon={AlertTriangle} label="Críticos ≤30d" value={d.inventario_criticos_30d ?? 0} sub="Ventana de devolución" tone="warning" />
        <Kpi icon={UserCheck} label="Incidencias abiertas" value={d.incidencias_abiertas ?? 0} sub="Pendientes de gestión" tone="default" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5 border border-[#d1dbd6]">
          <div className="flex items-center gap-3 mb-4">
            <Award className="text-[#205c45]" size={20} />
            <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>Top Clientes del mes</h2>
          </div>
          <table className="erp-table">
            <thead><tr><th>Cliente</th><th className="text-right">Monto</th><th className="text-right">Pedidos</th></tr></thead>
            <tbody>
              {(d.top_clientes || []).map((c, i) => (
                <tr key={i}>
                  <td className="font-medium">{c.cliente_nombre}</td>
                  <td className="text-right tabular-nums font-semibold">${c.monto.toFixed(2)}</td>
                  <td className="text-right tabular-nums">{c.pedidos}</td>
                </tr>
              ))}
              {(d.top_clientes || []).length === 0 && <tr><td colSpan={3} className="text-center py-6 text-[#8ba397]">Sin datos este mes</td></tr>}
            </tbody>
          </table>
        </Card>

        <Card className="p-5 border border-[#d1dbd6]">
          <div className="flex items-center gap-3 mb-4">
            <Users className="text-[#205c45]" size={20} />
            <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>Top Vendedores del mes</h2>
          </div>
          <table className="erp-table">
            <thead><tr><th>Vendedor</th><th className="text-right">Monto</th><th className="text-right">Pedidos</th></tr></thead>
            <tbody>
              {(d.top_vendedores || []).map((v, i) => (
                <tr key={i}>
                  <td className="font-medium">{v.vendedor_nombre}</td>
                  <td className="text-right tabular-nums font-semibold">${v.monto.toFixed(2)}</td>
                  <td className="text-right tabular-nums">{v.pedidos}</td>
                </tr>
              ))}
              {(d.top_vendedores || []).length === 0 && <tr><td colSpan={3} className="text-center py-6 text-[#8ba397]">Sin datos este mes</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Configuración de Márgenes */}
      <Card className="p-5 border border-[#d1dbd6]" data-testid="margenes-card">
        <div className="flex items-center gap-3 mb-4">
          <Percent className="text-[#205c45]" size={20} />
          <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>Márgenes por tipo de cliente</h2>
          <span className="text-xs text-[#8ba397]">Se aplica automático al facturar, salvo que el producto tenga un margen específico (override)</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {tiposCliente.map((t) => {
            const clave = t.codigo || (t.nombre || "").toLowerCase().replace(/\s+/g, "_");
            return (
              <div key={clave} className="border border-[#e9f2ee] rounded-md p-3 bg-[#fbfcfb]">
                <div className="text-xs uppercase font-semibold text-[#5c7368] mb-1">{t.nombre}</div>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="0.99"
                    placeholder="0.30 default"
                    value={margenes[clave] ?? ""}
                    onChange={(e) => setMargenes((m) => ({ ...m, [clave]: e.target.value }))}
                    onBlur={(e) => saveMargen(clave, e.target.value)}
                    className="h-9 tabular-nums"
                    data-testid={`margen-${clave}`}
                  />
                  <span className="text-sm text-[#5c7368] w-14">
                    {margenes[clave] ? `${(parseFloat(margenes[clave]) * 100).toFixed(0)}%` : <span className="text-[#8ba397] text-xs italic">30% def.</span>}
                  </span>
                </div>
                {savingKey === clave && <div className="text-[10px] text-[#8ba397] mt-1">Guardando...</div>}
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-[#5c7368]">
          💡 Regla de cálculo: <strong>precio_venta_unidad = costo × (1 + margen)</strong>. Si el producto tiene un margen específico (override), ese tiene precedencia sobre el tipo de cliente.
        </p>
      </Card>

      {/* Descarga de plantillas Excel */}
      <Card className="p-5 border border-[#d1dbd6]" data-testid="plantillas-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Download className="text-[#205c45]" size={20} />
            <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>Plantillas Excel para completar datos</h2>
          </div>
          <Button variant="outline" size="sm" onClick={regenerarPlantillas} className="gap-1" data-testid="regenerar-plantillas-btn">
            <RefreshCw size={14} /> Regenerar con datos actuales
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {plantillas.map((p) => (
            <div key={p.key} className="border border-[#e9f2ee] rounded-md p-3 bg-[#fbfcfb] flex items-start justify-between gap-3">
              <div>
                <div className="font-medium text-sm">{p.label}</div>
                <div className="text-xs text-[#5c7368] mt-0.5">{p.desc}</div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadPlantilla(p.key, p.filename)}
                data-testid={`download-${p.key}`}
                className="gap-1 shrink-0"
              >
                <Download size={14} /> Descargar
              </Button>
            </div>
          ))}
          {plantillas.length === 0 && <p className="text-xs text-[#8ba397] col-span-2">Cargando plantillas...</p>}
        </div>
      </Card>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone = "default" }) {
  const tones = {
    default: "bg-white border-[#d1dbd6]",
    warning: "bg-[#fef7ec] border-[#f5c477]",
    danger: "bg-[#fdecea] border-[#d99584]",
    primary: "bg-[#e9f2ee] border-[#b2d0c2]",
  };
  return (
    <div className={`rounded-md border p-5 ${tones[tone]}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#5c7368]">{label}</div>
        <Icon size={16} className="text-[#205c45]" />
      </div>
      <div className="text-2xl font-bold tabular-nums mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>{value}</div>
      <div className="text-xs text-[#5c7368]">{sub}</div>
    </div>
  );
}
